import { Config } from "final-form";

import { Product } from "@products/types";

const defaultAndroidVersion = "14";

const kioskModuleName = "com.emteria.kiosk";
const headlessModuleName = "com.emteria.headless";

const hasModule = (product: Product | undefined, moduleName: string) =>
    !!product?.customBuildConfig?.modules?.some((module) => module.name === moduleName);

export const isKioskModuleAdded = (product: Product | undefined) => hasModule(product, kioskModuleName);
export const isHeadlessModuleAdded = (product: Product | undefined) => hasModule(product, headlessModuleName);

const generateCodeName = (string: string) => {
    if (!string) {
        return "";
    }
    return string
        .replaceAll(" ", "_")
        .replaceAll(/[^0-9a-zA-Z_]+/g, "")
        .toLowerCase();
};
export const parseNightlyPeriod = (value: string | number | null | undefined): number => {
    const leadingDigits = /^\d*/.exec(String(value ?? ""))?.[0] ?? "";
    return leadingDigits === "" ? 0 : Number(leadingDigits);
};

// react-final-form turns an emptied input into undefined and JSON.stringify drops undefined keys, so a cleared
// field never reaches the backend at all. Both columns are nullable, so send an explicit null. Do not trim here:
// any value the backend stores differently from the typed one leaves the field dirty and the autosave repeating.
export const parseNullableText = (value: string | null | undefined): string | null =>
    value === "" || value === undefined ? null : value;

export const getInitialValues = (product: Product | undefined, workspaceId: number | undefined): Product => {
    if (!product) {
        return {
            id: 0,
            workspaceId: workspaceId || 0,
            isPublic: false,
            groupId: null,
            codename: "",
            androidVersion: defaultAndroidVersion,
            nightlyPeriod: 0,
            changes: null,
            visibility: [],
            customBuildConfig: {
                productName: "",
                rootDevice: "caas",
                modules: [],
                sources: {},
                manifest: { projects: [] },
                provisioningId: null,
                buildVariant: "user",
                compatibility: `>=${defaultAndroidVersion}`,
            },
            actionFeatures: ["Build"],
            documentationUrl: null,
            description: null,
        };
    }
    return {
        ...product,
        workspaceId: product.workspaceId || workspaceId || 0,
        codename: product.codename || generateCodeName(product.customBuildConfig?.productName ?? ""),
        isPublic: product.isPublic,
        actionFeatures: product.actionFeatures || ["Build"],
        documentationUrl: product.documentationUrl ?? null,
        description: product.description ?? null,
    };
};

export const mutators: Config<Product>["mutators"] = {
    setTouched: ([fieldName]: string[], state) => {
        if (state.fields[fieldName]) {
            state.fields[fieldName].touched = true;
        }
    },
    setUnTouched: ([fieldName]: string[], state) => {
        if (state.fields[fieldName]) {
            state.fields[fieldName].touched = false;
        }
    },
};
